import Markdown, { MarkdownToJSX } from 'markdown-to-jsx'
import React from 'react'

import classNames from 'classnames'
import Image from 'next/image'
import { getImageUrl } from 'utils/image'

interface MarkdownV2Props {
  children: string
  increasedMargins?: boolean
  overrides?: MarkdownToJSX.Overrides
}

export const MarkdownV2 = ({
  children,
  increasedMargins,
  overrides,
}: MarkdownV2Props) => {
  return (
    <Markdown
      options={{
        wrapper: React.Fragment,
        overrides: {
          h1: {
            props: {
              className:
                'text-2xl md:text-3xl font-normal font-body text-cool-800 mb-2 mt-6 first:mt-0 last:mb-0 tracking-normal',
            },
          },
          h2: {
            props: {
              className: classNames(
                'text-xl md:text-2xl font-normal font-body text-cool-800 first:mt-0 last:mb-0 tracking-normal mt-6',
                increasedMargins ? 'mb-4 lg:mb-6' : 'mb-2.5'
              ),
            },
          },
          h3: {
            props: {
              className:
                'text-lg leading-8 font-normal font-body text-cool-800 mb-2.5 mt-6 first:mt-0 last:mb-0 tracking-normal',
            },
          },
          h4: {
            props: {
              className:
                'text-base leading-7 md:leading-8 md:text-xl font-normal font-body text-cool-800 mb-1.5 mt-6 first:mt-0 last:mb-0 tracking-normal',
            },
          },
          p: {
            props: {
              className:
                'text-sm leading-6 sm:leading-7 sm:text-base font-normal font-body text-cool-800 mb-2.5 first:mt-0 last:mb-0',
            },
          },
          img: {
            component: ({ alt, src, ...props }) => {
              return (
                <div
                  className={classNames(
                    'relative mx-auto flex max-h-[1000px] w-full max-w-full flex-col pb-[100%]',
                    increasedMargins
                      ? 'mb-15 my-12 sm:mb-[4.25rem] lg:my-14'
                      : 'my-8 mb-11 sm:my-10 sm:mb-[3.25rem]'
                  )}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <Image
                    {...props}
                    fill
                    src={getImageUrl(src, 'large')}
                    alt={alt}
                    quality={50}
                    className='object-contain object-center'
                    sizes='(max-width: 900px) 80vw, 50vw'
                    fetchPriority='low'
                    loading='lazy'
                  />
                  {alt && (
                    <span className='absolute -bottom-6 mt-auto block w-full text-center text-sm italic text-cool-700'>
                      {alt}
                    </span>
                  )}
                </div>
              )
            },
            props: {
              className: 'mx-auto mb-3 max-w-full',
            },
          },
          a: {
            props: {
              className: 'text-secondary-default underline',
              target: '_blank',
            },
          },
          ul: {
            props: {
              className: 'list-disc list-inside pl-4 mb-6 first:mt-0 last:mb-0',
            },
          },
          li: {
            props: {
              className: 'text-base font-normal font-body text-cool-800',
            },
          },
          ...overrides,
        },
      }}
    >
      {children}
    </Markdown>
  )
}
